import {
    type Auth,
    type User,
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithCustomToken,
    AuthErrorCodes,
    UserCredential,
} from 'firebase/auth'
import { Functions, httpsCallable } from 'firebase/functions'
import type { FirebaseAuthConfig, FirebaseAuthState } from './types'
import { FirebaseError } from 'firebase/app'
import type {
    HandleGoogleAuthInputParams,
    HandleGoogleAuthResponse,
    InitializeEmailUserInput,
    InitializeEmailUserResponse,
    RefreshAuthTokenInput,
    RefreshAuthTokenResponse,
} from '~common/functions/auth'
import type { GetOnboardingQuestionnaireOutput } from '~common/onboarding/functions/types'

// These get injected by the Google Identity Services script that gets loaded on sign in request
declare global {
    interface Window {
        google: {
            accounts: {
                oauth2: {
                    initCodeClient: (config: {
                        client_id: string
                        scope: string
                        redirect_uri?: string
                        enable_granular_consent?: boolean
                        callback: (response: {
                            code?: string
                            error?: string
                        }) => void
                    }) => {
                        requestCode: () => void
                    }
                }
            }
        }
    }
}

export interface FirebaseAuthServiceDeps {
    auth: Auth
    config: FirebaseAuthConfig
    services: {
        _functions: Functions
    }
}

export class FirebaseAuthService {
    constructor(private readonly deps: FirebaseAuthServiceDeps) {}

    async getLoggedInUser(): Promise<User | null> {
        await this.deps.auth.authStateReady()
        return this.deps.auth.currentUser
    }

    async signUpWithEmail(
        email: string,
        password: string,
    ): Promise<
        | {
              success: true
          }
        | {
              success: false
              alreadyExists: boolean
              error: string
          }
    > {
        try {
            await createUserWithEmailAndPassword(
                this.deps.auth,
                email,
                password,
            )

            // const verifyEmail = httpsCallable<void, VerifyEmailResponse>(
            //     this.deps.services._functions,
            //     'verifyEmail',
            // )
            // await verifyEmail()
            const initializeEmailUser = httpsCallable<
                InitializeEmailUserInput,
                InitializeEmailUserResponse
            >(this.deps.services._functions, 'initializeEmailUser')
            await initializeEmailUser({
                email: email,
                password: password,
            })

            return {
                success: true,
            }
        } catch (error) {
            if (
                error instanceof FirebaseError &&
                error.code === AuthErrorCodes.EMAIL_EXISTS
            ) {
                return {
                    success: false,
                    alreadyExists: true,
                    error: 'Email already in use',
                }
            }
            return {
                success: false,
                alreadyExists: false,
                error: error instanceof Error ? error.message : `${error}`,
            }
        }
    }

    async loginWithEmail(
        email: string,
        password: string,
    ): Promise<{ success: true } | { success: false; error: string }> {
        try {
            await signInWithEmailAndPassword(this.deps.auth, email, password)
            return {
                success: true,
            }
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : `${error}`,
            }
        }
    }

    async signIn(customToken: string): Promise<UserCredential> {
        return await signInWithCustomToken(this.deps.auth, customToken)
    }

    async refreshAuthToken(
        googleAccountEmail: string,
    ): Promise<RefreshAuthTokenResponse> {
        const refreshAuthToken = httpsCallable<
            RefreshAuthTokenInput,
            RefreshAuthTokenResponse
        >(this.deps.services._functions, 'refreshAuthToken')
        const result = await refreshAuthToken({ googleAccountEmail })
        return {
            accessToken: result.data.accessToken,
            expiresAt: result.data.expiresAt,
        }
    }

    async loginWithGoogle(): Promise<{
        accessToken: string
        newUser: boolean
    }> {
        // Step 1: Get Google OAuth authorization code (via Google Identity Services)
        const authCode = await this.getGoogleAuthCode()

        // Step 2: Call our Firebase function to exchange code for tokens - both Google OAuth and Firebase Auth tokens
        const handleGoogleAuth = httpsCallable<
            HandleGoogleAuthInputParams,
            HandleGoogleAuthResponse
        >(this.deps.services._functions, 'handleGoogleAuth')

        const result = await handleGoogleAuth({ code: authCode })

        // Step 3: Sign in to Firebase with the custom token
        const { customToken, accessToken } = result.data

        await signInWithCustomToken(this.deps.auth, customToken)

        return {
            accessToken,
            newUser: result.data.newUser,
        }
    }

    private async getGoogleAuthCode(): Promise<string> {
        return new Promise((resolve, reject) => {
            // Load Google Identity Services if not already loaded
            if (!window.google) {
                const script = document.createElement('script')
                script.src = 'https://accounts.google.com/gsi/client'
                script.async = true
                script.defer = true
                script.onload = () => this.initiateGoogleOAuth(resolve, reject)
                script.onerror = () =>
                    reject(new Error('Failed to load Google Identity Services'))
                document.head.appendChild(script)
            } else {
                this.initiateGoogleOAuth(resolve, reject)
            }
        })
    }

    private initiateGoogleOAuth(
        resolve: (code: string) => void,
        reject: (error: Error) => void,
    ): void {
        const client = window.google.accounts.oauth2.initCodeClient({
            client_id: this.deps.config.clientId,
            scope: this.deps.config.scopes.join(' '),
            redirect_uri: window.location.origin,
            enable_granular_consent: true,
            callback: (response: { code?: string; error?: string }) => {
                if (response.error) {
                    reject(new Error(`OAuth error: ${response.error}`))
                    return
                }
                if (!response.code) {
                    reject(new Error('No authorization code received'))
                    return
                }
                resolve(response.code)
            },
        })

        client.requestCode()
    }

    async logout(): Promise<void> {
        // Clear onboarding cache on logout
        const currentUser = await this.getLoggedInUser()
        if (currentUser) {
            this.clearOnboardingCache(currentUser.uid)
        }

        await this.deps.auth.signOut()
    }

    async getUserQuestionnaire(userId: string): Promise<{
        userId: string
        answers: Record<string, unknown>
        onboardingComplete: boolean
    } | null> {
        // Check localStorage cache first
        const cachedData = localStorage.getItem(
            `onboarding_questionnaire_${userId}`,
        )
        if (cachedData) {
            try {
                const parsed = JSON.parse(cachedData)
                // If cached data shows onboarding is complete, return it to avoid network request
                if (parsed.onboardingComplete) {
                    return parsed
                }
            } catch (error) {
                // Invalid cache, remove it
                localStorage.removeItem(`onboarding_questionnaire_${userId}`)
            }
        }

        // Fetch from server
        const getOnboardingQuestionnaire = httpsCallable<
            { userId: string },
            GetOnboardingQuestionnaireOutput
        >(this.deps.services._functions, 'getOnboardingQuestionnaire')

        const result = await getOnboardingQuestionnaire({ userId })
        const questionnaire = result.data.questionnaire

        // Cache the data if onboarding is complete
        if (questionnaire?.onboardingComplete) {
            localStorage.setItem(
                `onboarding_questionnaire_${userId}`,
                JSON.stringify(questionnaire),
            )
        }

        return questionnaire
    }

    clearOnboardingCache(userId: string): void {
        localStorage.removeItem(`onboarding_questionnaire_${userId}`)
    }

    /**
     * Sets up a callback-based auth state listener
     * Returns an unsubscribe function to stop listening
     */

    onAuthStateChanged(
        setState: (authState: Partial<FirebaseAuthState>) => void,
    ): () => void {
        return onAuthStateChanged(this.deps.auth, (user) =>
            setState({
                user,
                error: null,
            }),
        )
    }
}
